import { useState } from "react";
import { Button, Empty } from "antd";
import {
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  HolderOutlined,
} from "@ant-design/icons";
import AIOffBadge from "./AIOffBadge";

/**
 * Ordered list of sections in the template being edited.
 *
 * Reorder via:
 *   1. Native HTML5 drag-and-drop on the grip handle (mouse / touch).
 *   2. Keyboard-accessible Up/Down buttons (WCAG fallback).
 *
 * HTML5 drag-and-drop was chosen over @dnd-kit/sortable for v1 to avoid
 * adding a new dependency. If the UX team wants animated transitions or
 * multi-item drag, we can swap in @dnd-kit later without changing the
 * component's public props.
 */
export default function TemplateCanvas({
  sections = [],
  selectedIndex,
  onSelectSection,
  onRemoveSection,
  onReorderSections,
}) {
  const [dragOverIndex, setDragOverIndex] = useState(null);

  if (!sections.length) {
    return (
      <div className="flex-1 bg-white flex items-center justify-center">
        <Empty
          description={
            <span className="text-gray-500">
              Click a section in the library on the left to add it (or drag it
              here). Remove a section with the trash icon, reorder with the grip
              handle.
            </span>
          }
        />
      </div>
    );
  }

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", String(index));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("text/plain");
    const fromIndex = parseInt(raw, 10);
    setDragOverIndex(null);
    if (Number.isFinite(fromIndex) && fromIndex !== dropIndex) {
      onReorderSections?.(fromIndex, dropIndex);
    }
  };

  return (
    <ol
      className="flex-1 bg-white p-4 overflow-y-auto space-y-2"
      onDragLeave={handleDragLeave}
    >
      {sections.map((section, index) => {
        const isSelected = selectedIndex === index;
        const isDropTarget = dragOverIndex === index;
        return (
          <li
            key={`${section.section_id}-${index}`}
            className={`border rounded p-3 flex items-start gap-3 cursor-pointer transition ${
              isSelected
                ? "border-blue-500 bg-blue-50"
                : isDropTarget
                ? "border-blue-300 bg-blue-50/30"
                : "border-gray-200 bg-white"
            }`}
            onClick={() => onSelectSection?.(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={(e) => handleDrop(e, index)}
            data-testid={`template-canvas-section-${index}`}
          >
            {/* Drag handle: only this is `draggable` so the rest of the card
                stays clickable for selection. */}
            <div
              className="flex flex-col gap-1 items-center"
              draggable
              onDragStart={(e) => {
                e.stopPropagation();
                handleDragStart(e, index);
              }}
              aria-label="Drag handle. Or use Up and Down arrows below."
              role="button"
              tabIndex={0}
            >
              <HolderOutlined className="text-gray-400 cursor-grab" />
              <Button
                size="small"
                type="text"
                icon={<ArrowUpOutlined />}
                disabled={index === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  onReorderSections?.(index, index - 1);
                }}
                aria-label="Move section up"
              />
              <Button
                size="small"
                type="text"
                icon={<ArrowDownOutlined />}
                disabled={index === sections.length - 1}
                onClick={(e) => {
                  e.stopPropagation();
                  onReorderSections?.(index, index + 1);
                }}
                aria-label="Move section down"
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-sm text-gray-900 truncate">
                  {section.display_name || section.section_id}
                </h4>
                {section.classification === "clinician_controlled" && <AIOffBadge />}
              </div>
              <div className="text-xs text-gray-500 mt-1 flex items-center gap-3">
                <span>{section.classification}</span>
                <span>·</span>
                <span>{section.style}</span>
                <span>·</span>
                <span>
                  AMD: {section.amd_field_name}
                  {section.amd_control_id
                    ? ` (${section.amd_control_id})`
                    : " (not yet provisioned)"}
                </span>
              </div>
            </div>

            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onRemoveSection?.(index);
              }}
              aria-label="Remove section"
            />
          </li>
        );
      })}
    </ol>
  );
}
